import { useState, useEffect } from 'react';
import { Target } from 'lucide-react';
import { motion } from 'framer-motion';
import { questionsApi } from '../../../services/api';
import IndustrySuccessFactorsChecklist from './IndustrySuccessFactorsChecklist';

interface IndustrySuccessFactorsSectionProps {
  companyId: string;
  onProgressChange: (progress: number) => void;
}

interface IndustrySuccessFactorsData {
  productAttributes: string[];
  serviceCharacteristics: string[];
  resources: string[];
  competitiveCapabilities: string[];
  shortcomings: string[];
}

const successFactorCategories = [
  {
    key: 'productAttributes' as keyof IndustrySuccessFactorsData,
    title: 'Product Attributes',
    description: 'On what basis do buyers choose between competing brands?',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    icon: '🎯',
    entityType: 'industry_success_factors.product_attributes',
    question: 'On what basis do buyers of your product/service choose between competing brands?',
  },
  {
    key: 'serviceCharacteristics' as keyof IndustrySuccessFactorsData,
    title: 'Service Characteristics',
    description: 'What service factors influence buyer decisions?',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    icon: '🤝',
    entityType: 'industry_success_factors.service_characteristics',
    question: 'What service characteristics do buyers value most?',
  },
  {
    key: 'resources' as keyof IndustrySuccessFactorsData,
    title: 'Resources',
    description: 'What resources must your company have to compete successfully?',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    icon: '💎',
    entityType: 'industry_success_factors.resources',
    question: 'Given the nature of competitive rivalry, what resources must your company have?',
  },
  {
    key: 'competitiveCapabilities' as keyof IndustrySuccessFactorsData,
    title: 'Competitive Capabilities',
    description: 'What capabilities are essential for success?',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    icon: '🚀',
    entityType: 'industry_success_factors.competitive_capabilities',
    question: 'What competitive capabilities must your company have to compete successfully?',
  },
  {
    key: 'shortcomings' as keyof IndustrySuccessFactorsData,
    title: 'Critical Shortcomings',
    description: 'What shortcomings would put you at a significant disadvantage?',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    icon: '⚠️',
    entityType: 'industry_success_factors.shortcomings',
    question:
      'What shortcomings are almost certain to put your company at a significant competitive disadvantage?',
  },
];

export default function IndustrySuccessFactorsSection({
  companyId,
  onProgressChange,
}: IndustrySuccessFactorsSectionProps) {
  const [successFactorsData, setSuccessFactorsData] = useState<IndustrySuccessFactorsData>({
    productAttributes: [],
    serviceCharacteristics: [],
    resources: [],
    competitiveCapabilities: [],
    shortcomings: [],
  });
  const [activeCategory, setActiveCategory] = useState('productAttributes');
  const [isLoading, setIsLoading] = useState(true);

  // Calculate progress based on answered questions: 5 answered questions = 100% completion
  const calculateProgress = () => {
    const totalItems = Object.values(successFactorsData).reduce(
      (sum, items) => sum + items.length,
      0
    );
    const targetAnswers = 5; // 5 answered questions = 100% completion
    return totalItems >= targetAnswers ? 100 : Math.round((totalItems / targetAnswers) * 100);
  };

  useEffect(() => {
    const progress = calculateProgress();
    onProgressChange(progress);
  }, [successFactorsData]); // Removed onProgressChange from deps - it's memoized with useCallback

  const handleSuccessFactorUpdate = (
    category: keyof IndustrySuccessFactorsData,
    items: string[]
  ) => {
    setSuccessFactorsData((prev) => ({
      ...prev,
      [category]: items,
    }));
  };

  // Load success factors data for all categories
  useEffect(() => {
    const loadSuccessFactorsData = async () => {
      try {
        setIsLoading(true);

        // Load data for all success factor categories
        const categoryData: IndustrySuccessFactorsData = {
          productAttributes: [],
          serviceCharacteristics: [],
          resources: [],
          competitiveCapabilities: [],
          shortcomings: [],
        };

        // Load questions for each success factor category
        for (const category of successFactorCategories) {
          try {
            const response = await questionsApi.getEntityQuestions(category.entityType, companyId);
            if (response.success && response.data) {
              // Extract selected items from responses
              const selectedItems = response.data
                .filter(
                  (q: any) =>
                    q.responses.length > 0 && q.responses[0].response.booleanValue === true
                )
                .map((q: any) => q.content);

              categoryData[category.key] = selectedItems;
            }
          } catch (error) {
            console.error(`Failed to load ${category.key} data:`, error);
          }
        }

        setSuccessFactorsData(categoryData);
      } catch (error) {
        console.error('Failed to load industry success factors data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSuccessFactorsData();
  }, [companyId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="strategy-framework-section">
      {/* Summary Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="strategy-framework-summary"
      >
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-gray-600">Total Success Factors:</span>
              <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                {Object.values(successFactorsData).reduce((sum, items) => sum + items.length, 0)}{' '}
                identified
              </span>
            </div>
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              {successFactorCategories.map((category) => (
                <div key={category.key} className="flex items-center space-x-1">
                  <span className="text-2xl">{category.icon}</span>
                  <span>{successFactorsData[category.key].length}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Two Column Layout - Categories Left, Wider Checklist Right */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="strategy-framework-grid"
      >
        {/* Left Side - Category Navigation */}
        <div className="strategy-framework-sidebar flex flex-col space-y-4">
          {/* Category Tab Navigation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center mb-4">
              <Target className="h-5 w-5 text-purple-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Success Factor Categories</h2>
              <div className="ml-auto text-right">
                <div className="text-sm text-gray-500">Select a category</div>
              </div>
            </div>

            <div className="space-y-2">
              {successFactorCategories.map((category) => {
                const itemCount = successFactorsData[category.key].length;
                const hasItems = itemCount > 0;

                return (
                  <button
                    key={category.key}
                    onClick={() => setActiveCategory(category.key)}
                    className={`w-full p-3 rounded-lg text-left transition-all ${
                      activeCategory === category.key
                        ? 'bg-purple-100 border-2 border-purple-300'
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{category.icon}</span>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{category.title}</div>
                          <div className="text-xs text-gray-600 mt-1">{category.description}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-2">
                        <div
                          className={`w-2 h-2 rounded-full ${hasItems ? 'bg-purple-500' : 'bg-gray-300'}`}
                        />
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            hasItems ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {itemCount} items
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Category Info Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-purple-500" />
              <h3 className="text-lg font-semibold text-gray-900">
                {successFactorCategories.find((cat) => cat.key === activeCategory)?.title}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              {successFactorCategories.find((cat) => cat.key === activeCategory)?.question}
            </p>
            <div className="bg-purple-50 rounded-lg p-3">
              <p className="text-sm text-purple-800">
                Check the factors that are critical for success in your industry. Focus on the most
                important competitive differentiators.
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Wider Checklist */}
        <div className="xl:col-span-3">
          {successFactorCategories.map((category) => (
            <div
              key={category.key}
              className={activeCategory === category.key ? 'block h-full' : 'hidden'}
            >
              <IndustrySuccessFactorsChecklist
                entityType={category.entityType}
                companyId={companyId}
                selectedItems={successFactorsData[category.key]}
                onSelectionChange={(items) => handleSuccessFactorUpdate(category.key, items)}
                color={category.color}
              />
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
